/**
 * How the particle orb answers an emoji cue: for a moment, a row of extra movement is added on top of
 * the state's (params.ts). A cue that plays once rises in 0.15 s, holds, then fades by `seconds`; a
 * timed one lasts its `sec`; a loop holds until the next cue. Talk and thinking cues need no row:
 * the face state already shows them.
 */
import type { CueName, FaceCue } from '../cues';
import type { StateParams } from './params';

export interface Reaction {
  boost: Partial<StateParams>;
  /** How long a `once` reaction lasts. */
  seconds: number;
}

export const REACTIONS: Partial<Record<CueName, Reaction>> = {
  happy: { boost: { grow: 0.1, spin: 0.35, alpha: 0.08 }, seconds: 1.6 },
  enthusiastic: { boost: { grow: 0.14, spin: 0.7, jitter: 0.5 }, seconds: 1.8 },
  celebration: { boost: { grow: 0.18, spin: 1.2, jitter: 1, ripple: 0.6 }, seconds: 3 },
  sad: { boost: { grow: -0.1, spin: -0.1, alpha: -0.3, breathe: 0.03 }, seconds: 2.4 },
  surprised: { boost: { grow: 0.2, ripple: 0.5 }, seconds: 0.9 },
  slight_smile: { boost: { grow: 0.06, spin: 0.12 }, seconds: 1.2 },
  reassuring: { boost: { breathe: 0.08, spin: -0.06, alpha: 0.05 }, seconds: 2.4 },
  confused: { boost: { jitter: 0.8, pulse: 0.5 }, seconds: 1.8 },
  wave: { boost: { ripple: 1, spin: 0.4 }, seconds: 1.6 },
  user_detected: { boost: { ripple: 0.6, grow: 0.08 }, seconds: 1.2 },
  tablet_on: { boost: { pulse: 0.6, ring: 0.35 }, seconds: 0 },
};

const ATTACK_S = 0.15;

/** How much of the cue's reaction applies `nowMs` (0..1), and the reaction itself. */
export function reactionAt(cue: FaceCue | null | undefined, nowMs: number): { reaction: Reaction; weight: number } | null {
  const reaction = cue?.name ? REACTIONS[cue.name] : undefined;
  if (!cue || !reaction) return null;
  const t = (nowMs - cue.at) / 1000;
  if (t < 0) return null;
  const rise = Math.min(1, t / ATTACK_S);
  if (cue.mode === 'loop' || reaction.seconds === 0) return { reaction, weight: rise };
  const length = cue.mode === 'timed' && cue.sec ? cue.sec : reaction.seconds;
  const hold = length * 0.4;
  const fall = t <= hold ? 1 : Math.max(0, 1 - (t - hold) / (length - hold));
  const weight = Math.min(rise, fall);
  return weight > 0 ? { reaction, weight } : null;
}

/** The state's row with the reaction added, by its weight. */
export function withReaction(params: StateParams, active: { reaction: Reaction; weight: number } | null): StateParams {
  if (!active) return params;
  const out = { ...params };
  for (const [key, value] of Object.entries(active.reaction.boost) as [keyof StateParams, number][]) out[key] += value * active.weight;
  return out;
}
